import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { Tick } from "../../icons/Tick";
import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { baseUrl } from "../../Environments/environment";
import {
	filterAddOnRequests,
	filterCheckInOutRequsts,
} from "../../redux/addOn/addOnSlice";
import moment from "moment";
import {
  FaBed,
  FaClock,
  FaCalendarPlus,
  FaGift,
  FaBroom,
  FaRegStar,
} from "react-icons/fa";
import { MdCleaningServices, MdLocalOffer } from "react-icons/md";
import { GiBroom } from "react-icons/gi";

// Helper to get icon by request type or name
function getRequestIcon(typeOrName) {
  if (typeOrName === "lateCheckOut")
    return <FaClock className="text-indigo-400 mr-2" />;
  if (typeOrName === "earlyCheckIn")
    return <FaBed className="text-green-400 mr-2" />;
  if (typeOrName === "extendStay")
    return <FaCalendarPlus className="text-yellow-400 mr-2" />;
  return <FaCalendarPlus className="text-gray-300 mr-2" />;
}

function getAddOnIcon(name) {
  if (typeof name === "string") {
    const lower = name.toLowerCase();
    if (lower.includes("housekeeping") || lower.includes("house keeping")) {
      return <FaBroom className="text-blue-400 mr-2" />;
    }
  }
  // Use a tag/offer icon for all other addOns
  return <MdLocalOffer className="text-yellow-400 mr-2" />;
}

const PreArrivalModal = ({ guest, getGuest, guestSessionId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [addOns, setAddOns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch();
  const storeAddons = useSelector((state) => state.addOn);
  const checkInOutRequests =
    useSelector((state) => state.addOn.guestCheckInOutRequests) || [];
  const [activeSticky, setActiveSticky] = useState(null);
  const stickyRefs = useRef({});

  useEffect(() => {
    getGuest();
  }, []);

  const getGuestCheckInOutRequests = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/guestApi/${guestSessionId}/checkInOutRequest`
      );
      console.log("Check In Out Requests", response.data.result);
      dispatch(filterCheckInOutRequsts(response.data.result));
    } catch (error) {
      console.error("Error fetching guest data:", error);
    }
  };

  const getGuestAddons = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${baseUrl}/guestApi/${guestSessionId}/addOns`
      );
      let result = response.data.result;
      // Convert object to array if needed
      if (result && !Array.isArray(result)) {
        result = Object.values(result);
      }
      dispatch(filterAddOnRequests(result));
      console.log("Add Ons", result);
      setAddOns(result || []);
    } catch (error) {
      console.error("Error fetching guest data:", error);
      setAddOns([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (guestSessionId) {
      getGuestCheckInOutRequests();
      getGuestAddons();
    }
  }, [guestSessionId]);

  function formatDateTime(dateTimeString) {
    const optionsDate = {
      year: "numeric",
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    };

		const optionsTime = {
			hour: "numeric",
			minute: "numeric",
			hour12: true,
			timeZone: "UTC",
		};

		const date = new Date(dateTimeString);
		const formattedDate = date.toLocaleDateString("en-US", optionsDate);
		const formattedTime = date.toLocaleTimeString("en-US", optionsTime);

    return `${formattedDate} - ${formattedTime}`;
  }

  function formatTimestamp(dateTimeString) {
    if (!dateTimeString) return null;
    const date = new Date(dateTimeString);
    return date.toLocaleString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }

  function getRequestTime(request, guest) {
    if (!request) return null;
    if (request.requestType === "earlyCheckIn")
      return request.earlyCheckInDateTime || guest.checkIn;
    if (request.requestType === "lateCheckOut")
      return request.lateCheckOutDateTime || guest.checkOut;
    if (request.requestType === "extendStay")
      return request.extendStayDateTime || guest.checkOut;
    return null;
  }

  // Check if there are any activities to show
  const hasActivities =
    guest.status.preArrivalStatus === "Applied" ||
    guest.status.earlyCheckInStatus !== "Not Requested" ||
    guest.status.lateCheckOutStatus !== "Not Requested" ||
    guest.status.extendStayStatus !== "Not Requested" ||
    (addOns && addOns.length > 0);

  // Helper to group by date
  function groupByDate(items) {
    return items.reduce((acc, item) => {
      const dateKey = item.createdAt
        ? moment(item.createdAt).format("YYYY-MM-DD")
        : "Unknown";
      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push(item);
      return acc;
    }, {});
  }

  // Combine addOns and checkInOutRequests
  const combinedItems = [
    ...checkInOutRequests.filter((req) =>
      ["earlyCheckIn", "lateCheckOut", "extendStay"].includes(req.requestType)
    ),
    ...addOns,
  ];

  const groupedByDate = groupByDate(combinedItems);
  const sortedDates = Object.keys(groupedByDate).sort(
    (a, b) => new Date(b) - new Date(a)
  );

  // Highlight sticky note for 2 seconds when it becomes active
  useEffect(() => {
    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const dateKey = entry.target.getAttribute("data-datekey");
            setActiveSticky(dateKey);
            setTimeout(() => {
              setActiveSticky((prev) => (prev === dateKey ? null : prev));
            }, 2000);
          }
        });
      },
      { root: null, threshold: 1.0 }
    );
    Object.values(stickyRefs.current).forEach((el) => {
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [sortedDates]);

  return (
    <Popover className="relative isolate shadow m-5">
      {({ open }) => (
        <>
          <div
            className="font-semibold text-gray-500 text-sm px-2 py-3 sticky top-0 bg-gradient-to-br from-gray-50 to-white z-10 rounded-t-xl flex items-center justify-between cursor-pointer select-none"
                onClick={() => setIsOpen(!isOpen)}
              >
            <span>All Your Activities</span>
                <ChevronDownIcon
                  aria-hidden="true"
              className={`h-5 w-5 ml-2 transition-transform duration-200 ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          </div>
          <div className="relative">
            <div
              className={`transition-all duration-700 ease-in-out ${
                isOpen
                  ? "opacity-100 translate-y-0 max-h-[1000px]"
                  : "opacity-0 -translate-y-4 max-h-0 pointer-events-none"
              } overflow-hidden`}
            >
                <PopoverPanel
                  static
                className="bg-gradient-to-br from-gray-50 to-white px-3 pb-3 shadow-lg ring-1 ring-gray-900/5 rounded-b-xl"
              >
                <div className="overflow-y-auto max-h-[70vh] px-1 pb-2 no-scrollbar">
                  {sortedDates.length > 0 ? (
                    sortedDates.map((dateKey) => {
                      let heading = moment(dateKey).calendar(null, {
                        sameDay: "[Today]",
                        lastDay: "[Yesterday]",
                        lastWeek: "dddd",
                        sameElse: "MMMM DD, YYYY (dddd)",
                      });
                      const daysAgo = moment().diff(moment(dateKey), "days");
                      if (daysAgo > 1 && daysAgo < 7) {
                        heading = moment(dateKey).format("dddd");
                      }
                      if (daysAgo >= 7) {
                        heading = moment(dateKey).format("MMMM DD, YYYY (dddd)");
                      }
                      return (
                        <div key={dateKey} className="w-full mb-0">
                          <div
                            className={`m-0 rounded font-bold text-indigo-600 text-xs tracking-wide shadow-sm flex justify-center items-center transition-all duration-500 ${
                              activeSticky === dateKey
                                ? "bg-indigo-100/80 shadow-lg scale-105"
                                : ""
                            }`}
                            style={{ minHeight: 18 }}
                          >
                            {heading}
                        </div>
                          <div className="space-y-0">
                            {groupedByDate[dateKey].map((item, idx) => {
                              const isRequest =
                                item.requestType &&
                                ["earlyCheckIn", "lateCheckOut", "extendStay"].includes(item.requestType);
                              let colorClass = "text-[#FB7185]";
                              if (item.requestStatus === "Accepted") colorClass = "text-[#10B981]";
                              else if (item.requestStatus === "Requested") colorClass = "text-[#F59E0B]";
                              return (
                                <div key={idx} className={`w-full py-1 ${idx !== groupedByDate[dateKey].length - 1 ? 'border-b border-gray-200' : ''}`} style={{ minHeight: 36 }}>
                                  <div className="flex items-center mb-0.5">
                                    {isRequest ? getRequestIcon(item.requestType) : getAddOnIcon(Array.isArray(item.name) ? item.name[0] : item.name)}
                                    <span className={`mt-1 text-lg ${colorClass}`}>
                                      {(() => {
                                        const label = isRequest
                                          ? (item.requestType === "earlyCheckIn" && "Early Check In") ||
                                            (item.requestType === "lateCheckOut" && "Late Check Out") ||
                                            (item.requestType === "extendStay" && "Extend Stay")
                                          : Array.isArray(item.name)
                                          ? item.name[0] ?? ""
                                          : item.name;
                                        return label;
                                      })()}
                                      {Number(item.price) > 0 && (
                                        <span className="ml-2 text-gray-500 font-medium text-sm">
                                          (${item.price})
                                        </span>
                                      )}
                                    </span>
                        </div>
                                  {item.description && (
                                    <div className="text-base text-[#111827]">
                                      {item.description}
                      </div>
                    )}
                                  <div className="flex justify-between items-end mt-0">
                                    <div className="text-base text-[#111827]">
                                      {isRequest
                                        ? formatDateTime(getRequestTime(item, guest))
                                        : item.timeDisplay || ""}
                        </div>
                                    {item.createdAt && (
                                      <span className="text-xs text-gray-400 font-mono">
                                        {formatTimestamp(item.createdAt)}
                                      </span>
                                )}
                              </div>
                            </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <div className="text-gray-400 mb-3">
                        <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                        </svg>
                      </div>
                      <div className="text-lg font-medium text-gray-600 mb-2">No Activities to Display</div>
                      <div className="text-sm text-gray-500">You haven't made any requests or added any services yet.</div>
                    </div>
                  )}
                </div>
                </PopoverPanel>
            </div>
          </div>
        </>
      )}
    </Popover>
  );
};

export default PreArrivalModal;

/* Hide scrollbar for Chrome, Safari and Opera */
<style jsx global>{`
  .no-scrollbar::-webkit-scrollbar {
    display: none;
  }
  .no-scrollbar {
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;  /* Firefox */
  }
`}</style>
